import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { TID } from "@/constants/testIds";
import {
  Users, Building2, Briefcase, FileText, CalendarClock, TrendingUp, DollarSign,
  UserCheck, ClipboardList, Plus, X, Check,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar } from "recharts";
import { toast } from "sonner";

function KpiCard({ icon: Icon, label, value, testId, hint }) {
  return (
    <div data-testid={testId} className="card-lift border border-slate-200 rounded-xl p-5 bg-white">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
          <div className="mt-2 font-display text-3xl font-bold text-navy">{value}</div>
          {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
        </div>
        <div className="h-10 w-10 rounded-lg bg-gold/10 grid place-items-center text-gold">
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default function AgencyDashboard() {
  const [data, setData] = useState(null);
  const [taskTitle, setTaskTitle] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/dashboard");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  const addTask = async (e) => {
    e.preventDefault();
    if (!taskTitle.trim()) return;
    await api.post("/admin/tasks", null, { params: { title: taskTitle } });
    setTaskTitle("");
    load();
    toast.success("Task added");
  };

  const toggleTask = async (t) => {
    await api.put(`/admin/tasks/${t.id}`, null, { params: { done: !t.done } });
    load();
  };

  const removeTask = async (t) => {
    await api.delete(`/admin/tasks/${t.id}`);
    load();
  };

  if (!data) return <div className="text-slate-500">Loading dashboard…</div>;
  const k = data.kpi;

  return (
    <div data-testid={TID.agencyDashboard}>
      <div className="mb-8">
        <div className="eyebrow mb-2">Agency Owner</div>
        <h1 className="font-display text-3xl font-bold text-navy">Business Overview</h1>
        <p className="text-slate-500 mt-1 text-sm">Everything running across your platform.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard icon={Users} label="Job seekers" value={k.total_candidates} testId={TID.agencyKpi("candidates")} />
        <KpiCard icon={Building2} label="Employers" value={k.total_employers} testId={TID.agencyKpi("employers")} />
        <KpiCard icon={Briefcase} label="Active jobs" value={k.active_jobs} testId={TID.agencyKpi("jobs")} />
        <KpiCard icon={FileText} label="New applications" value={k.new_applications} testId={TID.agencyKpi("apps")} />
        <KpiCard icon={CalendarClock} label="Upcoming interviews" value={k.upcoming_interviews} testId={TID.agencyKpi("interviews")} />
        <KpiCard icon={UserCheck} label="Candidates placed" value={k.candidates_placed} testId={TID.agencyKpi("placed")} />
        <KpiCard icon={ClipboardList} label="Pending employer approvals" value={k.pending_employer_approvals} testId={TID.agencyKpi("pending-emp")} />
        <KpiCard icon={DollarSign} label="Estimated revenue" value={`$${k.estimated_revenue.toLocaleString()}`} testId={TID.agencyKpi("est-rev")} hint={`Paid: $${k.completed_revenue.toLocaleString()}`} />
      </div>

      <div className="grid md:grid-cols-3 gap-6 mt-8">
        <div className="md:col-span-2 border border-slate-200 rounded-xl bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="eyebrow mb-1">Monthly performance</div>
              <div className="font-display font-semibold text-navy">Applications & placements — last 6 months</div>
            </div>
            <TrendingUp className="w-5 h-5 text-gold" />
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.monthly}>
                <CartesianGrid stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="month" stroke="#64748B" fontSize={12} />
                <YAxis stroke="#64748B" fontSize={12} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0" }} />
                <Bar dataKey="applications" fill="#0F172A" radius={[6,6,0,0]} />
                <Bar dataKey="placements" fill="#D4AF37" radius={[6,6,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border border-slate-200 rounded-xl bg-white p-6">
          <div className="eyebrow mb-2">Tasks & Reminders</div>
          <form onSubmit={addTask} className="flex gap-2 mb-3">
            <input value={taskTitle} onChange={e => setTaskTitle(e.target.value)}
              placeholder="Add a task…" className="flex-1 border border-slate-200 rounded-md px-3 py-2 text-sm" />
            <button type="submit" className="btn-navy py-2 px-3"><Plus className="w-4 h-4" /></button>
          </form>
          <div className="space-y-2 max-h-56 overflow-y-auto">
            {data.tasks.length === 0 && <div className="text-sm text-slate-500">No open tasks. 🎯</div>}
            {data.tasks.map(t => (
              <div key={t.id} className="flex items-center gap-2 text-sm">
                <button onClick={() => toggleTask(t)} className={`h-5 w-5 rounded border grid place-items-center ${t.done ? "bg-gold border-gold text-navy" : "border-slate-300"}`}>
                  {t.done && <Check className="w-3 h-3" />}
                </button>
                <div className={`flex-1 ${t.done ? "line-through text-slate-400" : "text-slate-700"}`}>{t.title}</div>
                <button onClick={() => removeTask(t)} className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-8 border border-slate-200 rounded-xl bg-white p-6">
        <div className="eyebrow mb-2">Recent Activity</div>
        <div className="divide-y divide-slate-100">
          {data.recent_activity.length === 0 && <div className="text-sm text-slate-500 py-4">No recent activity yet.</div>}
          {data.recent_activity.map((a, i) => (
            <div key={i} className="py-3 text-sm flex justify-between items-center">
              <div>
                <span className="font-medium text-navy">{a.action}</span>
                <span className="text-slate-500 ml-2 text-xs">{a.meta ? JSON.stringify(a.meta) : ""}</span>
              </div>
              <div className="text-xs text-slate-400">{new Date(a.created_at).toLocaleString()}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
